import styled, { CSSObject } from "styled-components";
// deps
import { ITheme } from "@swiggy-private/dls-web-common/dist/components/types.internal";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";
import { LINES_BACKGROUND_POPUP } from "@super-ui/components/m2c-swiggy-black-halfcard/constants";
import { getBackgroundImage } from "@super-ui/components/m2c-swiggy-black-halfcard/helpers";

const fadedBorder = {
    content: "''",
    display: "block",
    opacity: 0.3,
    height: "1px",
    width: "90%",
    margin: "0 auto",
    background:
        "linear-gradient(91.38deg, rgba(110, 80, 83, 0.2) 0.31%, rgba(255, 231, 183, 0.5) 48.98%, rgba(110, 80, 83, 0.2) 97.66%)",
};

export const HeaderContainer = styled.div<{ isPopup: boolean }>(({ isPopup }) => {
    return {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        ...(isPopup
            ? { flexDirection: "column" }
            : {
                  minHeight: "212px",
              }),
    };
});

export const InfoContent = styled.div(() => ({
    display: "flex",
    alignItems: "center",
    flexDirection: "column" as CSSObject["flexDirection"],
    textAlign: "center" as CSSObject["textAlign"],
}));

export const InfoContainer = styled.div`
    position: relative;
    overflow: hidden;
`;

export const ContentContainer = styled.div<{ isPopup: boolean; showDivider: boolean }>(
    ({ theme, isPopup, showDivider }) => ({
        marginTop: theme.spacing.xxSmall,
        display: "grid",
        gap: theme.spacing.xxSmall,
        justifyItems: "flex-start",
        opacity: 0,
        animation: isPopup ? "fadeIn 0s linear forwards" : "fadeIn 0s linear 2s forwards",

        "@keyframes fadeIn": {
            from: {
                opacity: 0,
            },
            to: {
                opacity: 1,
            },
        },

        ":after": {
            ...fadedBorder,
            marginTop: theme.spacing.small,
            opacity: `${showDivider ? 0.3 : 0}`,
            transition: "opacity 0.5s ease-in-out",
        },

        ...(isPopup && {
            ":before": {
                ...fadedBorder,
                marginTop: theme.spacing.xLarge,
                marginBottom: theme.spacing.medium,
            },
        }),
    }),
);

export const ContentItemContainer = styled.div<{ index: number; isPopup: boolean }>(({ theme, index, isPopup }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing.xSmall,
    opacity: 0,
    transform: "translateY(36px)",
    animation: "fadeUp 0.6s ease-in-out forwards",
    animationDelay: isPopup ? `${index * 0.1}s` : `${2 + index * 0.1}s`,

    "@keyframes fadeUp": {
        from: {
            opacity: 0,
            transform: "translateY(36px)",
        },
        to: {
            opacity: 1,
            transform: "translateY(0)",
        },
    },

    img: {
        position: "relative",
        top: theme.spacing.xxSmall,
    },
}));

export const CtaContainer = styled.div(({ theme }) => ({
    marginTop: theme.spacing.xLarge,
    width: "100%",
    height: "48px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
}));

export const ProgressBarWrapper = styled.div(({ theme }) => ({
    width: "100%",
    height: "25px",
    borderRadius: `0 0 ${theme.cornerRadius.xLarge}  ${theme.cornerRadius.xLarge}`,
    position: "relative",
    overflow: "hidden",
    marginTop: `calc(${theme.spacing.medium} * -1)`,
}));

const progressBarCommonStyles = (theme: ITheme) => ({
    height: theme.spacing.xSmall,
    borderRadius: theme.cornerRadius.xLarge,
    overflow: "hidden",
    left: "50%",
    transform: "translateX(-50%)",
});

export const ProgressBarContainer = styled.div<{ isPopup: boolean }>(({ isPopup, theme }) => {
    const commonStyles = progressBarCommonStyles(theme);
    if (isPopup) {
        return {
            position: "absolute" as CSSObject["position"],
            bottom: 0,
            width: "100%",
            ...commonStyles,
        };
    }
    return {
        position: "fixed" as CSSObject["position"],
        bottom: `calc(${theme.spacing.xxLarge} + var(--sab, 0px))`,
        width: "90%",
        background: theme.colors[theme.colorMode].primary_One_Blck,
        ...commonStyles,
    };
});

export const Progress = styled.div<{ duration: number; isPopup: boolean }>(({ duration, isPopup, theme }) => ({
    width: "100%",
    height: theme.spacing.xSmall,
    background: theme.colors[theme.colorMode].primary,
    animation: "progressAnimation forwards linear",
    animationDuration: `${duration}s`,
    borderRadius: isPopup ? theme.cornerRadius.small : 0,

    "@keyframes progressAnimation": {
        "0%": { width: "0px" },
        "100%": { width: "100%" },
    },
}));

export const PostLayoutWrapper = styled.div<{ isPopup: boolean }>(({ theme, isPopup }) => ({
    background: "linear-gradient(38deg, #10030C 20.37%, #460C39 115.64%)",
    borderTopLeftRadius: theme.cornerRadius.xLarge,
    borderTopRightRadius: theme.cornerRadius.xLarge,
    paddingBottom: isPopup ? 0 : `var(--sab, 0px)`,
    height: "100%",
    ...(isPopup && {
        borderRadius: theme.cornerRadius.xLarge,
    }),
}));

export const PostLayoutContainer = styled.div<{ isPopup: boolean }>(({ theme, isPopup }) => ({
    height: "100%",
    paddingTop: isPopup ? `calc(${theme.spacing.xxLarge} + ${theme.spacing.xSmall})` : "0",
    ...(isPopup && {
        backgroundImage: getBackgroundImage(LINES_BACKGROUND_POPUP, { width: 800 }),
        backgroundSize: "contain",
        backgroundRepeat: "no-repeat",
    }),
}));

export const Title = styled(Text)(({ theme }) => ({
    marginTop: theme.spacing.xxSmall,
    marginBottom: theme.spacing.xSmall,
}));

export const StarImage = styled(Image)`
    width: 20px;
    height: 20px;
    position: absolute;
    margin-top: 18px;
`;

export const LogoContainer = styled.div`
    width: 138px;
    height: 24px;
`;
